/**
 * MCP builders for the architectural categories whose render pipeline reads a
 * typed geometryData payload. yap_create_element routes Walls / Doors /
 * Windows through these so an agent can batch-create real, visible geometry
 * from explicit coordinates. Before this, a bare parameter-bag wall was
 * committed without geometryData and the wall-join solver crashed on the next
 * add ("Cannot read properties of undefined (reading 'start')"), poisoning
 * the whole document for subsequent wall operations.
 */

import * as THREE from 'three';
import { familyManager } from '../../core/FamilyManager';
import type {
    BIMElement,
    CompoundStructureLayer,
    Constraint,
    DoorGeometryData,
    FloorGeometryData,
    Level,
    RoofGeometryData,
    WallElement,
    WallGeometryData,
    WindowGeometryData,
} from '../../core/types';
import { asNumber, asString, asVec3 } from './mcpResultUtils';

type Args = Record<string, unknown>;

/** Defaults mirror the wall/door/window placement tools. */
const DEFAULT_WALL_TYPE = 'Basic Wall: Generic - 200mm';
const DEFAULT_DOOR_TYPE = 'Single-Flush: 0915 x 2134mm';
const DEFAULT_WINDOW_TYPE = 'Fixed: 0915 x 1830mm';

const FALLBACK_WALL_LAYERS: CompoundStructureLayer[] = [
    { function: 'Structure', materialId: 'Generic', thickness: 0.2, wraps: false },
];

const LOCATION_LINES: WallGeometryData['locationLine'][] = [
    'WallCenterline',
    'CoreCenterline',
    'FinishFaceExterior',
    'FinishFaceInterior',
    'CoreFaceExterior',
    'CoreFaceInterior',
];

export type BuilderResult<T> = { ok: true; value: T } | { ok: false; error: string };

/** Resolve "Family: Type" (or a bare type name) to a loaded family symbol. */
function resolveFamilySymbol(typeName: string) {
    const sep = typeName.indexOf(':');
    if (sep >= 0) {
        return familyManager.getType(typeName.slice(0, sep).trim(), typeName.slice(sep + 1).trim());
    }
    const hit = familyManager.getAllFamilies().find((f) => f.symbols.some((s) => s.name === typeName));
    return hit ? familyManager.getType(hit.name, typeName) : undefined;
}

/** Accept a level id or a level name; unknown values pass through untouched. */
function resolveLevelId(levels: Map<string, BIMElement>, value: string | undefined): string {
    if (!value) return '';
    if (levels.has(value)) return value;
    for (const [id, level] of levels) {
        if ((level as { name?: string }).name === value) return id;
    }
    return value;
}

function validTypeLayers(layers: unknown): CompoundStructureLayer[] | undefined {
    if (!Array.isArray(layers) || layers.length === 0) return undefined;
    const usable = layers.filter(
        (l): l is CompoundStructureLayer =>
            !!l && typeof l.thickness === 'number' && Number.isFinite(l.thickness) && typeof l.function === 'string',
    );
    return usable.length > 0 ? usable : undefined;
}

function wallContractError(what: string): string {
    return (
        `Invalid wall arguments (${what}). A wall needs start and end as [x, y, z] points in metres, ` +
        `e.g. { "category": "Walls", "type": "Basic Wall: Generic - 200mm", "start": [0, 0, 0], "end": [5, 0, 0], "height": 3 }. ` +
        `Optional: thickness (m), locationLine (WallCenterline | CoreCenterline | FinishFaceExterior | FinishFaceInterior | CoreFaceExterior | CoreFaceInterior), ` +
        `baseConstraintId / topConstraintId (level id or name, top default 'Unconnected'), baseOffset / topOffset (m), structural (bool).`
    );
}

function hostedContractError(category: 'Doors' | 'Windows', what: string): string {
    return (
        `Invalid ${category.slice(0, -1).toLowerCase()} arguments (${what}). ${category} need a host wall: ` +
        `pass hostWallId (a wall id from yap_create_element / yap_query_scene) and position (a 0–1 ratio along ` +
        `that wall, or a world [x, y, z] point that is projected onto it), ` +
        `e.g. { "category": "${category}", "type": "${category === 'Doors' ? DEFAULT_DOOR_TYPE : DEFAULT_WINDOW_TYPE}", "hostWallId": "<wall id>", "position": 0.5 }. ` +
        `Optional: width, height, sillHeight (metres).`
    );
}

export interface WallBuild {
    type: string;
    geometryData: WallGeometryData;
    parameters: Record<string, unknown>;
}

/** Build a complete wall geometryData + parameters from explicit args. */
export function buildWallElement(args: Args, levels: Map<string, BIMElement>): BuilderResult<WallBuild> {
    const geometry = (args.geometryData && typeof args.geometryData === 'object' ? args.geometryData : {}) as Args;
    const start = asVec3(args.start) ?? asVec3(geometry.start);
    const end = asVec3(args.end) ?? asVec3(geometry.end);
    if (!start) return { ok: false, error: wallContractError("missing 'start'") };
    if (!end) return { ok: false, error: wallContractError("missing 'end'") };
    // A wall's footprint comes from the ground-plane (X, Y) projection of its
    // location line. An end that collapses onto the start in plan (height
    // accidentally sent as the end Z, swapped coordinates, …) cannot build a
    // wall — reject with the fix baked into the message so the agent retries
    // instead of committing geometry the renderer cannot mean.
    if (Math.hypot(end[0] - start[0], end[1] - start[1]) < 0.01) {
        return {
            ok: false,
            error: wallContractError(
                `start and end project to the same ground point (${start[0]}, ${start[1]}) — ` +
                'a wall needs two DISTINCT [x, y] points for its plan run; the elevation belongs ' +
                'in baseOffset / height, not in the end Z',
            ),
        };
    }

    const type = asString(args.type) ?? DEFAULT_WALL_TYPE;
    const symbol = resolveFamilySymbol(type);
    const thicknessArg = asNumber(args.thickness);
    const layers = thicknessArg !== undefined
        ? [{ function: 'Structure' as const, materialId: 'Generic', thickness: thicknessArg, wraps: false }]
        : validTypeLayers(symbol?.parameters?.layers) ?? FALLBACK_WALL_LAYERS;
    const thickness = layers.reduce((sum, l) => sum + l.thickness, 0);

    const height = Math.max(asNumber(args.height) ?? asNumber(geometry.height) ?? 3, 0.01);
    const locArg = asString(args.locationLine);
    const locationLine = LOCATION_LINES.includes(locArg as WallGeometryData['locationLine'])
        ? (locArg as WallGeometryData['locationLine'])
        : 'WallCenterline';
    const baseConstraintId = resolveLevelId(levels, asString(args.baseConstraintId));
    const topArg = asString(args.topConstraintId);
    const topConstraintId = topArg === undefined ? 'Unconnected' : resolveLevelId(levels, topArg);
    const baseOffset = Math.max(asNumber(args.baseOffset) ?? 0, 0);
    const topOffset = asNumber(args.topOffset) ?? 0;
    const structural = args.structural === true;

    const geometryData: WallGeometryData = {
        start,
        end,
        height,
        thickness,
        layers,
        baseOffset,
        baseConstraintId,
        topConstraintId,
        topOffset,
        locationLine,
    };

    const length = new THREE.Vector3(...start).distanceTo(new THREE.Vector3(...end));
    const parameters: Record<string, unknown> = {
        Length: `${length.toFixed(2)} m`,
        'Unconnected Height': `${height.toFixed(2)} m`,
        'Base Constraint': baseConstraintId,
        'Top Constraint': topConstraintId,
        'Base Offset': `${(baseOffset * 1000).toFixed(0)} mm`,
        'Top Offset': `${(topOffset * 1000).toFixed(0)} mm`,
        'Location Line': locationLine,
        Structural: structural,
        'Room Bounding': !structural,
        Width: `${thickness.toFixed(3)} m`,
    };

    return { ok: true, value: { type, geometryData, parameters } };
}

export interface HostedOpeningBuild {
    type: string;
    geometryData: DoorGeometryData | WindowGeometryData;
    parameters: Record<string, unknown>;
    constraints: Constraint[];
}

/** Build a hosted door/window geometryData + parameters from explicit args. */
export function buildHostedOpeningElement(
    args: Args,
    category: 'Doors' | 'Windows',
    elements: Map<string, BIMElement>,
): BuilderResult<HostedOpeningBuild> {
    const hostId = asString(args.hostWallId) ?? asString(args.host) ?? asString(args.wallId);
    if (!hostId) return { ok: false, error: hostedContractError(category, "missing 'hostWallId'") };

    const host = elements.get(hostId);
    if (!host || host.category !== 'Walls') {
        return { ok: false, error: hostedContractError(category, `no wall found with id '${hostId}' — create walls first, then reference their ids`) };
    }
    const hostGeometry = (host as WallElement).geometryData;
    if (!Array.isArray(hostGeometry?.start) || !Array.isArray(hostGeometry?.end)) {
        return { ok: false, error: hostedContractError(category, `host wall '${hostId}' has no usable geometry`) };
    }

    let position = asNumber(args.position);
    if (position === undefined) {
        const point = asVec3(args.point) ?? asVec3(args.position) ?? asVec3((args.geometryData as Args | undefined)?.position);
        if (point) {
            const start = new THREE.Vector3(...hostGeometry.start);
            const run = new THREE.Vector3(...hostGeometry.end).sub(start);
            const lengthSq = run.lengthSq();
            position = lengthSq < 1e-12 ? 0 : new THREE.Vector3(...point).sub(start).dot(run) / lengthSq;
        }
    }
    if (position === undefined || !Number.isFinite(position)) {
        return { ok: false, error: hostedContractError(category, "missing 'position'") };
    }
    position = THREE.MathUtils.clamp(position, 0, 1);

    const type = asString(args.type) ?? (category === 'Doors' ? DEFAULT_DOOR_TYPE : DEFAULT_WINDOW_TYPE);
    const symbol = resolveFamilySymbol(type);
    const symbolParams = (symbol?.parameters ?? {}) as Record<string, unknown>;
    const defaultWidth = 0.915;
    const width = Math.max(asNumber(args.width) ?? asNumber(symbolParams['Width']) ?? defaultWidth, 0.01);
    const height = Math.max(
        asNumber(args.height) ?? asNumber(symbolParams['Height']) ?? (category === 'Doors' ? 2.134 : 1.83),
        0.01,
    );
    const sillHeight = Math.max(asNumber(args.sillHeight) ?? (category === 'Doors' ? 0 : 0.915), 0);

    const geometryData: DoorGeometryData | WindowGeometryData = { hostWallId: hostId, position, width, height, sillHeight };
    const parameters: Record<string, unknown> = {
        Width: `${width.toFixed(2)} m`,
        Height: `${height.toFixed(2)} m`,
        'Sill Height': `${(sillHeight * 1000).toFixed(0)} mm`,
        Host: hostId,
        Level: (host as WallElement).parameters?.['Base Constraint'] ?? '',
        ...(category === 'Doors' ? { 'Flip Facing': false, 'Flip Hand': false } : {}),
    };
    const constraints: Constraint[] = [
        { type: 'HOST_CONTAINED', targetId: hostId, data: { positionAlongHost: position } },
    ];

    return { ok: true, value: { type, geometryData, parameters, constraints } };
}

// ---------------------------------------------------------------------------
// Horizontal elements (Floors / Ceilings / Roofs) + Levels + Dimensions
// ---------------------------------------------------------------------------

/** [x, y] pair from [x, y(, z)] input (the render boundary is a plan polygon). */
function asVec2(value: unknown): [number, number] | undefined {
    const arr = Array.isArray(value)
        ? value
        : typeof value === 'string'
            ? value.split(/[\s,]+/)
            : null;
    if (!arr || arr.length < 2) return undefined;
    const x = asNumber(arr[0]);
    const y = asNumber(arr[1]);
    return x === undefined || y === undefined ? undefined : [x, y];
}

/** Closed plan polygon: ≥3 points; Z components are dropped if present. */
function asBoundaryLoop(value: unknown): [number, number][] | undefined {
    if (!Array.isArray(value)) return undefined;
    const pts = value.map(asVec2);
    if (pts.some((p) => p === undefined)) return undefined;
    const loop = pts as [number, number][];
    return loop.length >= 3 ? loop : undefined;
}

/** Resolve a level (id or name) and its elevation, which the store keeps in mm. */
function resolveLevel(levels: Map<string, Level>, value: string | undefined): { id: string; elevationMm: number } | undefined {
    if (!value) return undefined;
    const direct = levels.get(value);
    if (direct) return { id: direct.id, elevationMm: direct.elevation };
    const lowered = value.toLowerCase();
    for (const level of levels.values()) {
        if (level.name.toLowerCase() === lowered) return { id: level.id, elevationMm: level.elevation };
    }
    return undefined;
}

function slabContractError(category: 'Floors' | 'Ceilings', what: string): string {
    return (
        `Invalid ${category.toLowerCase()} arguments (${what}). ${category} need a closed plan polygon: ` +
        `boundary as 3+ [x, y] or [x, y, z] points in metres (counterclockwise), e.g. ` +
        `{ "category": "${category}", "boundary": [[0,0],[6,0],[6,4],[0,4]] }. ` +
        `Optional: levelId (level id or name), baseOffset (metres above the level, ceilings default 2.6), ` +
        `thickness (metres, default 0.2), layers ([{ function, materialId, thickness }]), name.`
    );
}

export interface SlabBuild {
    type: string;
    geometryData: FloorGeometryData;
    parameters: Record<string, unknown>;
}

/** Build a Floors/Ceilings geometryData + parameters from explicit args. */
export function buildSlabElement(
    args: Args,
    category: 'Floors' | 'Ceilings',
    levels: Map<string, Level>,
): BuilderResult<SlabBuild> {
    const geometry = (args.geometryData && typeof args.geometryData === 'object' ? args.geometryData : {}) as Args;
    const boundary = asBoundaryLoop(args.boundary) ?? asBoundaryLoop(geometry.boundary);
    if (!boundary) return { ok: false, error: slabContractError(category, "missing or invalid 'boundary'") };

    const type = asString(args.type) ?? category;
    const symbol = resolveFamilySymbol(type);
    const layers = validTypeLayers(symbol?.parameters?.layers)
        ?? validTypeLayers(args.layers)
        ?? [{ function: 'Structure' as const, materialId: 'Generic', thickness: category === 'Ceilings' ? 0.05 : 0.2, wraps: false }];
    const thickness = Math.max(
        asNumber(args.thickness) ?? layers.reduce((sum, l) => sum + l.thickness, 0),
        0.01,
    );

    const level = resolveLevel(levels, asString(args.levelId) ?? asString(args.baseConstraintId));
    // Ceilings hang below: the renderer's default bottom face is 2.6 m.
    const defaultOffset = category === 'Ceilings' ? 2.6 : 0;
    const baseOffset = asNumber(args.baseOffset) ?? defaultOffset;
    // The renderer reads heightOffsetFromLevel in metres and does NOT add the
    // level elevation itself, so fold a resolved level's elevation in here.
    const heightOffsetFromLevel = baseOffset + (level ? level.elevationMm / 1000 : 0);

    const geometryData: FloorGeometryData = {
        boundary,
        thickness,
        layers,
        levelId: level?.id ?? '',
        heightOffsetFromLevel,
    };
    const parameters: Record<string, unknown> = {
        'Height Offset From Level': `${(heightOffsetFromLevel * 1000).toFixed(0)} mm`,
        Thickness: `${thickness.toFixed(3)} m`,
        Level: level?.id ?? '',
        'Room Bounding': category === 'Floors',
        Area: `${polygonArea(boundary).toFixed(2)} m²`,
    };
    return { ok: true, value: { type, geometryData, parameters } };
}

function polygonArea(loop: [number, number][]): number {
    let area = 0;
    for (let i = 0; i < loop.length; i++) {
        const [x1, y1] = loop[i];
        const [x2, y2] = loop[(i + 1) % loop.length];
        area += x1 * y2 - x2 * y1;
    }
    return Math.abs(area / 2);
}

function roofContractError(what: string): string {
    return (
        `Invalid roof arguments (${what}). A roof needs a closed plan polygon: ` +
        `footprint (or boundary) as 3+ [x, y] or [x, y, z] points in metres, e.g. ` +
        `{ "category": "Roofs", "footprint": [[0,0],[6,0],[6,4],[0,4]], "roofType": "flat" }. ` +
        `Optional: roofType (flat | gable | hip | shed, default flat), slope (degrees, default 0), ` +
        `levelId (level id or name), baseOffsetFromLevel (metres above the level), thickness (metres), name.`
    );
}

export interface RoofBuild {
    type: string;
    geometryData: RoofGeometryData;
    parameters: Record<string, unknown>;
}

/** Build a Roofs geometryData + parameters from explicit args. */
export function buildRoofElement(args: Args, levels: Map<string, Level>): BuilderResult<RoofBuild> {
    const geometry = (args.geometryData && typeof args.geometryData === 'object' ? args.geometryData : {}) as Args;
    const footprint = asBoundaryLoop(args.footprint)
        ?? asBoundaryLoop(geometry.footprint)
        // Accept `boundary` as an alias so Floor- and Roof-style payloads
        // stop being two different contracts for the agent.
        ?? asBoundaryLoop(args.boundary)
        ?? asBoundaryLoop(geometry.boundary);
    if (!footprint) return { ok: false, error: roofContractError("missing or invalid 'footprint'") };

    const type = asString(args.type) ?? 'Roofs';
    const rawRoofType = (asString(args.roofType) ?? asString(geometry.roofType) ?? 'flat').toLowerCase();
    const ROOF_TYPES: Record<string, RoofGeometryData['roofType']> = {
        flat: 'Flat', gable: 'Gable', hip: 'Hip', shed: 'Shed',
    };
    const roofType = ROOF_TYPES[rawRoofType];
    if (!roofType) {
        return { ok: false, error: roofContractError(`roofType '${rawRoofType}' — expected flat | gable | hip | shed`) };
    }
    const slope = Math.max(asNumber(args.slope) ?? (roofType === 'Flat' ? 0 : 30), 0);
    const level = resolveLevel(levels, asString(args.levelId) ?? asString(args.baseLevelId));
    const baseOffsetFromLevel = (asNumber(args.baseOffsetFromLevel) ?? asNumber(args.baseOffset) ?? 0)
        + (level ? level.elevationMm / 1000 : 0);
    const thickness = Math.max(asNumber(args.thickness) ?? 0.3, 0.01);

    const geometryData: RoofGeometryData = {
        footprint,
        slope,
        overhang: Math.max(asNumber(args.overhang) ?? 0, 0),
        baseLevelId: level?.id ?? '',
        baseOffsetFromLevel,
        roofType,
        definesSlope: footprint.map(() => roofType !== 'Flat' && slope > 0),
    };
    const parameters: Record<string, unknown> = {
        'Base Offset From Level': `${(baseOffsetFromLevel * 1000).toFixed(0)} mm`,
        'Roof Type': roofType,
        Slope: `${slope.toFixed(1)}°`,
        Thickness: `${thickness.toFixed(3)} m`,
        Area: `${polygonArea(footprint).toFixed(2)} m²`,
    };
    return { ok: true, value: { type, geometryData, parameters } };
}

export interface LevelBuild {
    name: string;
    elevation: number;
}

/**
 * Levels are not scene elements — they live in project.levels. The builder
 * only validates the contract; the handler commits through bimKernel.addLevel.
 * Elevation is in millimetres (BIM-style store convention; Level 2 = 3000).
 */
export function buildLevelElement(args: Args, levels: Map<string, Level>): BuilderResult<LevelBuild> {
    const name = asString(args.name) ?? `Level ${levels.size + 1}`;
    const elevation = asNumber(args.elevation);
    if (elevation === undefined) {
        return {
            ok: false,
            error: "Invalid level arguments (missing 'elevation'). A level needs a name and an elevation in millimetres, e.g. { \"category\": \"Levels\", \"name\": \"Level 2\", \"elevation\": 3000 }.",
        };
    }
    const duplicate = Array.from(levels.values()).some(
        (l) => l.name.toLowerCase() === name.toLowerCase() || l.elevation === elevation,
    );
    if (duplicate) {
        return {
            ok: false,
            error: `A level named '${name}' or with elevation ${elevation} mm already exists. Reuse it (reference by id/name in other elements) instead of duplicating it.`,
        };
    }
    return { ok: true, value: { name, elevation } };
}

export interface DimensionBuild {
    type: string;
    geometryData: Record<string, unknown>;
    parameters: Record<string, unknown>;
}

/** Build an aligned-dimension geometryData (kind: "aligned") from refs. */
export function buildDimensionElement(args: Args): BuilderResult<DimensionBuild> {
    const geometry = (args.geometryData && typeof args.geometryData === 'object' ? args.geometryData : {}) as Args;
    const rawRefs = Array.isArray(args.refs) ? args.refs : Array.isArray(geometry.refs) ? geometry.refs : undefined;
    const refs = (rawRefs ?? []).map(asVec3);
    if (!rawRefs || refs.length < 2 || refs.some((r) => r === undefined)) {
        return {
            ok: false,
            error: 'Invalid dimension arguments (missing refs). A dimension needs refs: 2+ [x, y, z] points in metres, e.g. { "category": "Dimensions", "refs": [[0,0,0],[6,0,0]], "offset": 0.6 }. Optional: offset (metres the dim line sits off the refs), text.',
        };
    }
    const points = refs as [number, number, number][];
    const offset = Math.max(asNumber(args.offset) ?? 0.6, 0.01);
    // Offset the dimension line perpendicular to the ref run, in plan.
    const first = points[0];
    const last = points[points.length - 1];
    const runX = last[0] - first[0];
    const runY = last[1] - first[1];
    const len = Math.hypot(runX, runY) || 1;
    const nx = -runY / len;
    const ny = runX / len;
    const shift = (p: [number, number, number]): [number, number, number] => [p[0] + nx * offset, p[1] + ny * offset, p[2]];
    const dimStart = asVec3(args.dimStart) ?? shift(first);
    const dimEnd = asVec3(args.dimEnd) ?? shift(last);
    const text = asString(args.text);

    const geometryData: Record<string, unknown> = { kind: 'aligned', refs: points, dimStart, dimEnd };
    const parameters: Record<string, unknown> = {
        ...(text ? { Value: text } : {}),
        Offset: `${offset.toFixed(2)} m`,
    };
    return { ok: true, value: { type: asString(args.type) ?? 'Aligned Dimensions', geometryData, parameters } };
}
